import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { usePageStore } from './page'
import { useConfigStore } from './config'
import { setCookie, getCookie } from './utils/helpers'

export type SlotPrediction = {
  common?: number,
  mobile?: number,
  desktop?: number
}

export type AdSizePrediction = {
  adZones?: string[],
  generationTime?: string,
  [zone: string]: SlotPrediction | string[] | string | undefined
}

export type AdSlotMarkerPayload = {
  offsetTop: number,
  [key: string]: unknown
}

export type AdSlotMarker = Record<number, AdSlotMarkerPayload>

const forceTestZoneByCookie = (): boolean => {
  return getCookie('forceTestZone') === 'develop'
}

export const useAdaStore = defineStore('ada', () => {
  // Stores
  const pageStore = usePageStore()
  const configStore = useConfigStore()

  // State
  const previewBundle = ref(false)
  const forceTestZone = ref(false)
  const dynamicAdSlotsAllocated = ref(false) // Indicates if dynamic ad slots have been allocated
  const ncbTopPlacedInArticle = ref(false) // Indicates if the native crossbar top was placed within the article content
  const asyncContentMode = ref(false) // Indicates if async content mode is enabled
  const asyncContentRefresh = ref(false) // Indicates if async content refresh is in progress
  const asyncContentReadyForAdReload = ref(false) // Indicates if async content is ready for ad reload
  const adSizePrediction = ref<AdSizePrediction>({})
  const predictedMinHeights = ref<Record<string, number>>({}) // effective (post-fallback) min-heights, keyed by slot name, as actually used for the ada-size-prediction CSS
  const adDefendScript = ref<string | null>(null)
  const adSlotMarker = ref<AdSlotMarker>({})
  const activatedAdSlotMarker = ref<AdSlotMarker>({})

  // Getters
  const blockAds = computed(() => {
    return pageStore.pageData?.metaData?.disallowAds || ((process.env.AD_FREE_BRANDS as unknown as string[]) ?? []).includes(pageStore.brandFromStore)
  })

  const staticMobile1OnHome = computed(() => {
    return pageStore.pageData?.pageMeta?.url === '/'
  })

  const dynamicAdSlotsEnabled = computed(() => {
    return !!configStore.rsConfig?.adSlotUsage?.dynamicAdSlots?.enabled
  })

  const asideEnabled = computed(() => {
    return !!configStore.rsConfig?.adSlotUsage?.dynamicAdSlots?.enabled && (pageStore.isArticleOrArticleVersion || pageStore.pageData?.metaData?.behaveLikeArticle)
  })

  const loadPreviewBundle = computed(() => {
    return previewBundle.value
  })

  const isForceTestZone = computed(() => {
    return forceTestZone.value
  })

  const isAsyncContentMode = computed(() => {
    return asyncContentMode.value
  })

  const isAsyncContentReadyForAdReload = computed(() => {
    return asyncContentReadyForAdReload.value
  })

  const getAdSlotMarker = computed(() => {
    return adSlotMarker.value
  })

  const getActivatedAdSlotMarker = computed(() => {
    return activatedAdSlotMarker.value
  })

  const getAdDefendScript = computed(() => {
    return !!configStore.rsConfig?.adSlotUsage?.adDefendBackfill && adDefendScript.value ? adDefendScript.value : null
  })

  // Actions
  const setPreviewBundle = (newPreviewBundle: boolean): void => {
    previewBundle.value = newPreviewBundle
  }

  const setForceTestZone = (newEnabled: boolean): void => {
    forceTestZone.value = newEnabled
  }

  const setDynamicAdSlotsAllocated = (newEnabled: boolean): void => {
    dynamicAdSlotsAllocated.value = newEnabled
  }

  const setNcbTopPlacedInArticle = (newEnabled: boolean): void => {
    ncbTopPlacedInArticle.value = newEnabled
  }

  const setAsyncContentMode = (newEnabled: boolean): void => {
    asyncContentMode.value = newEnabled
  }

  const setAsyncContentReadyForAdReload = (newEnabled: boolean): void => {
    asyncContentReadyForAdReload.value = newEnabled
  }

  const forcePreviewBundle = (): void => {
    setPreviewBundle(true)
  }

  const setTestZone = (testZone: string): void => {
    setCookie('forceTestZone', testZone, new Date(Date.now() + 1000 * 60 * 60 * 24))
    setForceTestZone(testZone === 'develop')
  }

  const setAsyncContentRefresh = (newEnabled: boolean): void => {
    asyncContentRefresh.value = newEnabled
  }

  const setAdSizePrediction = (payload: AdSizePrediction | null): void => {
    adSizePrediction.value = payload || {}
  }

  const setPredictedMinHeights = (payload: Record<string, number>): void => {
    predictedMinHeights.value = payload
  }

  const setAdDefendScript = (payload: string | null): void => {
    adDefendScript.value = payload || null
  }

  const addAdSlotMarker = (payload: AdSlotMarkerPayload): void => {
    adSlotMarker.value[payload.offsetTop] = payload
  }

  const flushAdSlotMarker = (): void => {
    adSlotMarker.value = {}
  }

  const setActivatedAdSlotMarker = (payload: AdSlotMarker): void => {
    activatedAdSlotMarker.value = payload
  }

  const flushActivatedAdSlotMarker = (): void => {
    activatedAdSlotMarker.value = {}
  }

  const checkForceTestZoneCookie = (): void => {
    setForceTestZone(forceTestZoneByCookie())
  }

  return {
    previewBundle,
    forceTestZone,
    dynamicAdSlotsAllocated,
    ncbTopPlacedInArticle,
    asyncContentMode,
    asyncContentRefresh,
    isAsyncContentReadyForAdReload,
    adSizePrediction,
    predictedMinHeights,
    adDefendScript,
    adSlotMarker,
    activatedAdSlotMarker,
    blockAds,
    staticMobile1OnHome,
    loadPreviewBundle,
    isForceTestZone,
    isAsyncContentMode,
    dynamicAdSlotsEnabled,
    asideEnabled,
    getAdSlotMarker,
    getActivatedAdSlotMarker,
    getAdDefendScript,
    setPreviewBundle,
    setForceTestZone,
    setDynamicAdSlotsAllocated,
    setNcbTopPlacedInArticle,
    setAsyncContentMode,
    setAsyncContentRefresh,
    setAsyncContentReadyForAdReload,
    forcePreviewBundle,
    setTestZone,
    setAdSizePrediction,
    setPredictedMinHeights,
    setAdDefendScript,
    addAdSlotMarker,
    flushAdSlotMarker,
    setActivatedAdSlotMarker,
    flushActivatedAdSlotMarker,
    checkForceTestZoneCookie
  }
})
